import { Link } from 'react-router-dom';
import { ExternalLink, Pencil, Plus } from 'lucide-react';
import type { Target } from '@shared/protocol';
import { useAuth } from '../lib/auth';
import { useDocumentTitle } from '../lib/branding';
import { useTargets } from '../lib/useTargets';

function summary(t: Target): string[] {
  const s = t.spec;
  const out: string[] = [];
  if (s.loudness) out.push(`${s.loudness.integrated} LUFS (±${s.loudness.tolerance} LU), max ${s.loudness.maxTruePeak} dBTP${s.loudness.maxShortTerm !== null ? `, short-term max ${s.loudness.maxShortTerm} LUFS` : ''}${s.loudness.maxLra !== null ? `, LRA max ${s.loudness.maxLra} LU` : ''}`);
  if (s.video.codecs) out.push(`Video ${s.video.codecs.join('/')}${s.video.scan ? `, ${s.video.scan}` : ''}${s.video.fieldOrder ? ` ${s.video.fieldOrder.toUpperCase()}` : ''}${s.video.keyframeIntervalMax ? `, keyframes at most every ${s.video.keyframeIntervalMax} s` : ''}${s.video.rateControl ? `, ${s.video.rateControl}` : ''}`);
  else if (s.video.scan || s.video.formats) out.push(`Video ${s.video.formats?.map((f) => `${f.width}x${f.height}`).join(', ') ?? ''} ${s.video.scan ?? ''} ${s.video.fieldOrder?.toUpperCase() ?? ''}`.trim());
  if (s.audio.codecs || s.audio.sampleRates) out.push(`Audio ${[s.audio.codecs?.join('/'), s.audio.sampleRates?.map((r) => `${r / 1000} kHz`).join(' or '), s.audio.channels?.map((c) => (c === 2 ? 'stereo' : c === 6 ? '5.1' : `${c} ch`)).join(' or ')].filter(Boolean).join(', ')}`);
  if (s.srt) out.push(`SRT: loss up to ${s.srt.maxLossPercent} %, RTT up to ${s.srt.maxRttMs} ms`);
  return out;
}

export function PresetsPage() {
  useDocumentTitle('Presets and profiles');
  const targets = useTargets();
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  const sections = [
    { title: 'Broadcaster presets', text: 'Delivery requirements for broadcasters and destinations. Fill in each broadcaster\'s exact values from its delivery specification.', list: targets.filter((t) => t.kind === 'broadcaster') },
    { title: 'Platform profiles', text: 'Online platform ingest requirements, taken from each platform\'s published encoder settings on the date shown. Platforms change these; check the source and update the profile when they do.', list: targets.filter((t) => t.kind === 'platform') },
  ];
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Presets and profiles</h1>
          <p className="muted">What each stream can be checked against. Select them per stream on the stream's edit page.</p>
        </div>
        {admin && (
          <Link className="btn btn-primary" to="/presets/new">
            <Plus size={16} aria-hidden /> New preset or profile
          </Link>
        )}
      </div>
      {sections.map((sec) => (
        <section key={sec.title} className="preset-section">
          <h2>{sec.title}</h2>
          <p className="muted small">{sec.text}</p>
          <div className="preset-grid">
            {sec.list.map((t) => (
              <article key={t.id} className="card preset-card">
                <header className="card-header">
                  <h3 className="preset-name">{t.name}</h3>
                  {admin && (
                    <Link className="btn btn-sm btn-ghost" to={`/presets/${t.id}`} aria-label={`Edit ${t.name}`}>
                      <Pencil size={14} aria-hidden /> Edit
                    </Link>
                  )}
                </header>
                <div className="card-body">
                  <p className="small">{t.description}</p>
                  <ul className="preset-facts small">
                    {summary(t).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  {(t.sourceUrls.length > 0 || t.checkedAt) && (
                    <p className="muted small preset-source">
                      {t.sourceUrls.map((u) => (
                        <a key={u} href={u} target="_blank" rel="noreferrer noopener">
                          {new URL(u).hostname} <ExternalLink size={12} aria-hidden />
                        </a>
                      ))}
                      {t.checkedAt && <span>Checked {t.checkedAt}</span>}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
